
import axios from 'axios';

import API_URL from '../../config';

export class AdminService {

    /**
     * Obtener listado de todas las empresas (SaaS Dashboard)
     */
    static async getEmpresas() {
        try {
            const response = await axios.get(`${API_URL}/api/v2/admin/empresas`);
            return response.data; // Array de empresas
        } catch (error) {
            console.error('Error fetching empresas:', error);
            throw error.response?.data?.error || 'Error al obtener empresas';
        }
    }

    /**
     * Registrar nueva Empresa + Admin (Transaccional)
     * @param {Object} data - { empresa: {...}, admin: {...} }
     */
    static async createEmpresa(data) {
        try {
            const response = await axios.post(`${API_URL}/api/v2/admin/empresas`, data);
            return response.data;
        } catch (error) {
            console.error('Error creating empresa:', error);
            throw error.response?.data?.error || 'Error al crear empresa';
        }
    }

    /**
     * Actualizar datos de empresa
     */
    static async updateEmpresa(id, data) {
        try {
            const response = await axios.put(`${API_URL}/api/v2/admin/empresas/${id}`, data);
            return response.data;
        } catch (error) {
            console.error('Error updating empresa:', error);
            throw error.response?.data?.error || 'Error al actualizar empresa';
        }
    }

    /**
     * Cambiar estado de empresa (Activa/Suspendida)
     */
    static async toggleEstadoEmpresa(id, nuevoEstado) {
        try {
            const response = await axios.put(`${API_URL}/api/v2/admin/empresas/${id}/estado`, {
                estado: nuevoEstado
            });
            return response.data;
        } catch (error) {
            throw error.response?.data?.error || 'Error al actualizar estado';
        }
    }

    /**
     * Obtener los datos de Mi Junta (la empresa a la que pertenece el usuario autenticado)
     */
    static async getMiJunta() {
        try {
            const token = localStorage.getItem('token');
            const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
            const response = await axios.get(`${API_URL}/api/v2/juntas`, config);
            return response.data;
        } catch (error) {
            console.error('Error fetching mi junta:', error);
            throw error.response?.data?.error || 'Error al obtener datos de mi junta';
        }
    }

    /**
     * Actualizar los datos de Mi Junta (incluyendo documentos)
     */
    static async updateMiJunta(formData) {
        try {
            const token = localStorage.getItem('token');
            const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
            // formData automatically sets Content-Type to multipart/form-data via axios
            const response = await axios.put(`${API_URL}/api/v2/juntas`, formData, config);
            return response.data;
        } catch (error) {
            console.error('Error updating mi junta:', error);
            throw error.response?.data?.error || 'Error al actualizar datos de mi junta';
        }
    }

    /**
     * Obtener usuarios de una empresa específica
     */
    static async getUsuariosEmpresa(id) {
        try {
            const response = await axios.get(`${API_URL}/api/v2/admin/empresas/${id}/usuarios`);
            return response.data;
        } catch (error) {
            throw error.response?.data?.error || 'Error al obtener usuarios';
        }
    }

    static async createUsuarioEmpresa(id, userData) {
        try {
            const response = await axios.post(`${API_URL}/api/v2/admin/empresas/${id}/usuarios`, userData);
            return response.data;
        } catch (error) {
            throw error.response?.data?.error || 'Error al crear usuario';
        }
    }

    static async getAllUsuarios() {
        try {
            const token = localStorage.getItem('token');
            const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
            // Updated to use the general endpoint which handles RBAC (Super Admin gets all, Company Admin gets theirs)
            const response = await axios.get(`${API_URL}/api/v2/usuarios`, config);
            return response.data;
        } catch (error) {
            console.error('[AdminService] getAllUsuarios error:', error);
            throw error.response?.data?.error || 'Error al obtener usuarios globales';
        }
    }

    static async createUsuario(userData) {
        try {
            const response = await axios.post(`${API_URL}/api/v2/usuarios`, userData);
            return response.data;
        } catch (error) {
            throw error.response?.data?.error || 'Error al crear usuario';
        }
    }

    static async updateUsuario(id, userData) {
        try {
            const response = await axios.put(`${API_URL}/api/v2/usuarios/${id}`, userData);
            return response.data;
        } catch (error) {
            throw error.response?.data?.error || 'Error al actualizar usuario';
        }
    }

    static async getRoles() {
        try {
            const response = await axios.get(`${API_URL}/api/v2/admin/roles`);
            return response.data;
        } catch (error) {
            throw error.response?.data?.error || 'Error al obtener roles';
        }
    }

    static async getAllTarifas() {
        try {
            const token = localStorage.getItem('token');
            const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
            const response = await axios.get(`${API_URL}/api/v2/tarifas`, config);
            return response.data;
        } catch (error) {
            console.error('Error fetching global tarifas:', error);
            throw error.response?.data?.error || 'Error al obtener tarifas';
        }
    }

    // ========== Medidores ==========
    static async getAllMedidores(page = 1, limit = 25, search = '', sortBy = 'fecha', sortOrder = 'DESC') {
        try {
            const response = await axios.get(`${API_URL}/api/v2/medidores`, {
                params: { page, limit, search, sortBy, sortOrder }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching medidores:', error);
            throw error.response?.data?.error || 'Error al obtener medidores';
        }
    }

    static async importarMedidores(file) {
        try {
            const formData = new FormData();
            formData.append('file', file);
            const response = await axios.post(`${API_URL}/api/v2/medidores/importar`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error importing medidores:', error);
            throw error.response?.data?.error || 'Error al importar medidores';
        }
    }

    static async createMedidor(data) {
        try {
            const response = await axios.post(`${API_URL}/api/v2/medidores`, data);
            return response.data;
        } catch (error) {
            console.error('Error creating medidor:', error);
            throw error.response?.data?.error || 'Error al crear medidor';
        }
    }

    static async updateMedidor(codigoMedidor, data) {
        try {
            const response = await axios.put(`${API_URL}/api/v2/medidores/${codigoMedidor}`, data);
            return response.data;
        } catch (error) {
            console.error('Error updating medidor:', error);
            throw error.response?.data?.error || 'Error al actualizar medidor';
        }
    }

    static async deleteMedidor(codigoMedidor) {
        try {
            const response = await axios.delete(`${API_URL}/api/v2/medidores/${codigoMedidor}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting medidor:', error);
            throw error.response?.data?.error || 'Error al eliminar medidor';
        }
    }

    // ========== Clientes ==========
    static async getAllClientes(includeInactive = false, page = 1, limit = 25, search = '', sortBy = 'fecha_registro', sortOrder = 'DESC', onlyInactive = false) {
        try {
            const response = await axios.get(`${API_URL}/api/v2/clientes`, {
                params: { includeInactive, page, limit, search, sortBy, sortOrder, onlyInactive }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching clientes:', error);
            throw error.response?.data?.error || 'Error al obtener clientes';
        }
    }

    static async createCliente(data) {
        try {
            const response = await axios.post(`${API_URL}/api/v2/clientes`, data);
            return response.data;
        } catch (error) {
            console.error('Error creating cliente:', error);
            throw error.response?.data?.error || 'Error al crear cliente';
        }
    }

    static async updateCliente(id, data) {
        try {
            const response = await axios.put(`${API_URL}/api/v2/clientes/${id}`, data);
            return response.data;
        } catch (error) {
            console.error('Error updating cliente:', error);
            throw error.response?.data?.error || 'Error al actualizar cliente';
        }
    }

    static async deleteCliente(id) {
        try {
            const response = await axios.delete(`${API_URL}/api/v2/clientes/${id}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting cliente:', error);
            throw error.response?.data?.error || 'Error al eliminar cliente';
        }
    }

    static async importarClientes(file) {
        try {
            const formData = new FormData();
            formData.append('file', file);
            const response = await axios.post(`${API_URL}/api/v2/clientes/importar`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error importing clientes:', error);
            throw error.response?.data?.error || 'Error al importar clientes';
        }
    }

    static async getClienteByCodigo(codigo) {
        try {
            const response = await axios.get(`${API_URL}/api/v2/clientes/codigo/${codigo}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching cliente by codigo:', error);
            throw error.response?.data?.error || 'Cliente no encontrado';
        }
    }

    // ========== Consumos ==========
    static async getAllConsumos(page = 1, limit = 10, search = '', sortBy = 'fecha', sortOrder = 'DESC', fechaInicio = null, fechaFin = null) {
        try {
            const response = await axios.get(`${API_URL}/api/v2/consumos`, {
                params: { page, limit, search, sortBy, sortOrder, fechaInicio, fechaFin }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching consumos:', error);
            throw error.response?.data?.error || 'Error al obtener consumos';
        }
    }

    static async getConsumosByMedidor(codigoMedidor, page = 1, limit = 10) {
        try {
            const response = await axios.get(`${API_URL}/api/v2/consumos/medidor/${codigoMedidor}`, {
                params: { page, limit }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching consumos by medidor:', error);
            throw error.response?.data?.error || 'Error al obtener consumos del medidor';
        }
    }

    static async createConsumo(data) {
        try {
            const response = await axios.post(`${API_URL}/api/v2/consumos`, data);
            return response.data;
        } catch (error) {
            console.error('Error creating consumo:', error);
            throw error.response?.data?.error || 'Error al crear consumo';
        }
    }

    static async updateConsumo(id, data) {
        try {
            const response = await axios.put(`${API_URL}/api/v2/consumos/${id}`, data);
            return response.data;
        } catch (error) {
            console.error('Error updating consumo:', error);
            throw error.response?.data?.error || 'Error al actualizar consumo';
        }
    }

    static async deleteConsumo(id) {
        try {
            const response = await axios.delete(`${API_URL}/api/v2/consumos/${id}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting consumo:', error);
            throw error.response?.data?.error || 'Error al eliminar consumo';
        }
    }

    // Helper: Obtener información del medidor incluyendo última lectura
    static async getMedidorInfo(codigoMedidor) {
        try {
            const response = await axios.get(`${API_URL}/api/v2/consumos/medidor-info/${codigoMedidor}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching medidor info:', error);
            throw error.response?.data?.error || 'Medidor no encontrado';
        }
    }

    static async searchClientes(query) {
        try {
            const response = await axios.get(`${API_URL}/api/v2/clientes`, {
                params: {
                    search: query,
                    limit: 100
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error searching clientes:', error);
            throw error.response?.data?.error || 'Error al buscar clientes';
        }
    }

    static async getMedidoresByCliente(clienteId) {
        try {
            const response = await axios.get(`${API_URL}/api/v2/medidores`, {
                params: { id_cliente: clienteId }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching medidores by cliente:', error);
            throw error.response?.data?.error || 'Error al obtener medidores del cliente';
        }
    }

    // ============================================
    // FINANZAS / RENDICIÓN DE CUENTAS
    // ============================================

    /**
     * Obtener lista de movimientos financieros (Ingresos, Egresos, Deudas)
     * @param {Object} filters - { tipo, fechaInicio, fechaFin }
     */
    static async getAllMovimientos(filters = {}) {
        try {
            const response = await axios.get(`${API_URL}/api/v2/finanzas`, {
                params: filters
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching movimientos:', error);
            throw error.response?.data?.error || 'Error al obtener movimientos';
        }
    }

    /**
     * Crear nuevo movimiento financiero
     * @param {Object} data - { tipo, descripcion, cantidad, precioUnitario, precioTotal, fecha }
     */
    static async createMovimiento(data) {
        try {
            const response = await axios.post(`${API_URL}/api/v2/finanzas`, data);
            return response.data;
        } catch (error) {
            console.error('Error creating movimiento:', error);
            throw error.response?.data?.error || 'Error al crear movimiento';
        }
    }

    /**
     * Actualizar movimiento financiero existente
     */
    static async updateMovimiento(id, data) {
        try {
            const response = await axios.put(`${API_URL}/api/v2/finanzas/${id}`, data);
            return response.data;
        } catch (error) {
            console.error('Error updating movimiento:', error);
            throw error.response?.data?.error || 'Error al actualizar movimiento';
        }
    }

    /**
     * Eliminar movimiento financiero (soft delete)
     */
    static async deleteMovimiento(id) {
        try {
            await axios.delete(`${API_URL}/api/v2/finanzas/${id}`);
            return true;
        } catch (error) {
            console.error('Error deleting movimiento:', error);
            throw error.response?.data?.error || 'Error al eliminar movimiento';
        }
    }

    /**
     * Obtener balance contable (totales de ingresos, egresos, deudas)
     * @param {string} fechaInicio - Fecha inicio (opcional)
     * @param {string} fechaFin - Fecha fin (opcional)
     */
    static async getBalance(fechaInicio, fechaFin) {
        try {
            const response = await axios.get(`${API_URL}/api/v2/finanzas/balance`, {
                params: { fechaInicio, fechaFin }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching balance:', error);
            throw error.response?.data?.error || 'Error al obtener balance';
        }
    }

    // ========== Reportes ==========
    static async getReporteEconomico(fechaInicio, fechaFin) {
        try {
            const response = await axios.get(`${API_URL}/api/v2/reportes/economico`, {
                params: { fechaInicio, fechaFin }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching reporte economico:', error);
            throw error.response?.data?.error || 'Error al obtener reporte económico';
        }
    }

    static async getReporteMedidores() {
        try {
            const response = await axios.get(`${API_URL}/api/v2/reportes/medidores`);
            return response.data;
        } catch (error) {
            console.error('Error fetching reporte medidores:', error);
            throw error.response?.data?.error || 'Error al obtener reporte de medidores';
        }
    }

    static async getReporteMora() {
        try {
            const response = await axios.get(`${API_URL}/api/v2/reportes/mora`);
            return response.data;
        } catch (error) {
            console.error('Error fetching reporte mora:', error);
            throw error.response?.data?.error || 'Error al obtener reporte de mora';
        }
    }

    static async getReporteSumaTotal(fecha) {
        try {
            const response = await axios.get(`${API_URL}/api/v2/pagos/suma-total`, {
                params: { fecha }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching reporte suma total:', error);
            throw error.response?.data?.error || 'Error al obtener reporte de suma total';
        }
    }
}

